// helpers/MessageHelpers.js
// Subscription-driven UI signals (ticker messages, notifications) — both are the
// same shape of thing: a DataStore change comes in via subscribeToModel's shared
// next(recordChange) callback, and this decides what, if anything, to surface.

import { ViewType, ActionType } from "../Enums.js";
import { ModelType } from "../models";

// Must match the row/cell flash animation duration in Datagrid.css.
export const NOTIFICATION_FLASH_DURATION_MS = 3000;

/**
 * inferModelType — used by enqueueTickerMessage when a Logs record doesn't carry
 * its own modelType.
 */
function inferModelType({ type, element, reviewingTokens, logger }) {
  if (type === "Submissions") {
    return reviewingTokens.includes(element.formToken) ? ModelType.REVIEW : ModelType.SUBMISSION;
  }

  const key = element.modelType || type.toUpperCase();
  logger?.log?.("inferModelType:", "element.modelType:", element.modelType || type.toUpperCase());
  logger?.log?.("inferModelType:", "key:", key);
  return ModelType[key] || ModelType.UNKNOWN;
}

/**
 * enqueueTickerMessage — subscription-coupled, moved here from DatastoreHelpers.js.
 * Behaviour preserved (including early return after Logs branch).
 */
export function enqueueTickerMessage({
  changeType,
  recordChange,
  setTickerItems,

  currentProjectToken,
  reviewingTokens,

  logger,
  appUser, // enhanced user if needed later (currently not used)
}) {
  const { opType, element } = recordChange;
  const id = element.id;

  const createdAtMs = (() => {
    const ts = element.createdAt;
    const ms = ts ? Date.parse(ts) : NaN;
    return Number.isFinite(ms) ? ms : Date.now();
  })();

  if (changeType === "Logs") {
    const isTicker = element.isTicker === true;
    if (!isTicker) return;

    const model =
      element.modelType ||
      inferModelType({
        type: changeType,
        element,
        reviewingTokens,
        logger,
      });

    const action = element.category || element.type || opType;
    const text = element.description || element.logData?.message || id || "Unknown";

    const msg = {
      id: `${model}-${action}-${id}-${createdAtMs}`,
      recordType: "Logs",
      model,
      action,
      text,
      createdAt: createdAtMs,
      remainingCycles: 3,
    };

    logger?.log?.("enqueueTickerMessage:", "LOG msg:", msg);

    setTickerItems((prev) => {
      const next = [...prev, msg];
      const truncated = next.length > 10 ? next.slice(-10) : next;
      logger?.log?.("enqueueTickerMessage:", `tickerItems length now: ${truncated.length}`);
      return truncated;
    });

    return;
  }

  // id/opType/version/lastChangedAt included so repeated firings for the same
  // underlying record (vs. genuinely separate records) can be told apart — see
  // DEBUG log below and docs/TODO.md re: possible duplicate Projects firings.
  const eventIdentity = { id, opType, version: element._version, lastChangedAt: element._lastChangedAt };

  if (changeType === "Projects") {
    logger?.log?.(
      "enqueueTickerMessage:",
      "Projects subscription - nothing I can think of that is not covered by an Oz finished log",
      eventIdentity
    );
  } else if (changeType === "Assets") {
    logger?.log?.(
      "enqueueTickerMessage:",
      "Assets subscription - nothing I can think of that is not covered by an Oz finished log",
      eventIdentity
    );
  } else if (changeType === "Submissions") {
    if (opType !== ActionType.INSERT) {
      logger?.log?.("enqueueTickerMessage:", "Submissions subscription - non-insert change, no ticker item", eventIdentity);
      return;
    }

    const model = inferModelType({ type: changeType, element, reviewingTokens, logger });

    const msg = {
      id: `${model}-${ActionType.INSERT}-${id}-${createdAtMs}`,
      recordType: changeType,
      model,
      action: ActionType.INSERT,
      createdAt: createdAtMs,
      remainingCycles: 3,
    };

    logger?.log?.("enqueueTickerMessage:", "Submissions INSERT msg:", msg);

    setTickerItems((prev) => {
      const next = [...prev, msg];
      const truncated = next.length > 10 ? next.slice(-10) : next;
      return truncated;
    });
  } else {
    logger?.log?.("enqueueTickerMessage:", "Change type not recognised:", changeType, eventIdentity);
  }
}

/**
 * Shared by enqueueNotification and enqueueMetaUsersNotifications below — both add
 * a notification then need it removed again once the row/cell flash (a fixed-
 * duration CSS animation) would have finished.
 */
function scheduleNotificationExpiry(msg, setNotifications) {
  setTimeout(() => {
    setNotifications((prev) => prev.filter((n) => n.id !== msg.id));
  }, NOTIFICATION_FLASH_DURATION_MS);
}

/**
 * enqueueNotification — Users-only for now (see docs/TODO.md). Only processes a
 * Logs change when it's for a User (modelType) AND the currently active view is
 * the Users view — i.e. "if we are in a view that matches the notification, we
 * process it". No notification-worthy/type distinction yet (also see docs/TODO.md)
 * — every matching Logs entry is treated identically.
 */
export function enqueueNotification({
  changeType,
  recordChange,
  setNotifications,
  currentView,
  logger,
}) {
  if (changeType !== "Logs") return;

  const { element } = recordChange;

  if (element.modelType !== ModelType.USER || currentView?.type !== ViewType.USERS) return;

  const msg = {
    id: element.id,
    entityId: element.entityId,
    modelType: element.modelType,
    createdAt: element.createdAt,
  };

  logger?.log?.("enqueueNotification:", "New notification:", msg);

  setNotifications((prev) => [...prev, msg]);

  // Row/cell flash is a CSS animation with its own fixed duration (see Datagrid.css) —
  // this cleans the entry out of state once that animation would have finished, so
  // the array doesn't grow forever. The bell (reading the same array via
  // fetchUsersViewData) disappears at the same time for now — see docs/TODO.md re:
  // making the bell persist independently of the flash.
  scheduleNotificationExpiry(msg, setNotifications);
}

/**
 * One-level diff, shared by diffUserFields/diffSubmissionFields — flat objects
 * only, not a generic deep-diff (see docs/TODO.md).
 */
function shallowDiff(prevObj, nextObj) {
  const fields = new Set([
    ...Object.keys(prevObj || {}),
    ...Object.keys(nextObj || {}),
  ]);

  const changes = [];
  fields.forEach((field) => {
    const prevValue = prevObj?.[field];
    const nextValue = nextObj?.[field];
    if (JSON.stringify(prevValue) === JSON.stringify(nextValue)) return;
    changes.push({ field, prevValue, nextValue });
  });
  return changes;
}

function diffUserFields(prevUser, nextUser) {
  return shallowDiff(prevUser, nextUser).filter((c) => c.field !== "id"); // identity, not a change
}

/**
 * enqueueMetaUsersNotifications — separate source from enqueueNotification above.
 * Some per-user fields (e.g. specialist_areas) live in Projects.meta.users[], not
 * on the Users model itself — editing them shows up as a Projects UPDATE, not a
 * Logs entry, so they'd never be caught by enqueueNotification's Logs-only check.
 * Diffs the previous vs next meta.users array (by id) and raises one notification
 * per user entry that actually changed. Only meta.users is compared — unrelated
 * Projects changes (config, theme, deadlines, etc.) are ignored. Same
 * "every change treated identically" simplification as enqueueNotification — see
 * docs/TODO.md.
 */
export function enqueueMetaUsersNotifications({
  prevProject,
  nextProject,
  setNotifications,
  currentView,
  logger,
}) {
  if (currentView?.type !== ViewType.USERS) return;

  const nextUsers = nextProject?.meta?.users;
  if (!Array.isArray(nextUsers)) return;

  const prevUsers = Array.isArray(prevProject?.meta?.users) ? prevProject.meta.users : [];
  const prevById = new Map(prevUsers.map((u) => [u.id, u]));

  nextUsers.forEach((nextUser) => {
    const prevUser = prevById.get(nextUser.id);
    if (JSON.stringify(prevUser) === JSON.stringify(nextUser)) return;

    const msg = {
      id: `metaUser-${nextUser.id}-${Date.now()}`,
      entityId: nextUser.id,
      modelType: ModelType.USER,
      createdAt: new Date().toISOString(),
      changes: diffUserFields(prevUser, nextUser),
    };

    logger?.log?.("enqueueMetaUsersNotifications:", "New notification:", msg);

    setNotifications((prev) => [...prev, msg]);
    scheduleNotificationExpiry(msg, setNotifications);
  });
}

// One-level diff over data + meta, same technique as diffUserFields.
function diffSubmissionFields(prevSubmission, nextSubmission) {
  return [
    ...shallowDiff(prevSubmission?.data, nextSubmission?.data),
    ...shallowDiff(prevSubmission?.meta, nextSubmission?.meta),
  ];
}

// Submissions equivalent of enqueueMetaUsersNotifications — not Logs-gated, no reliable Logs entry for a submission edit.
export function enqueueSubmissionNotifications({
  prevSubmission,
  nextSubmission,
  setNotifications,
  currentView,
  logger,
}) {
  if (currentView?.type !== ViewType.SUBMISSIONS) return;
  if (!nextSubmission) return;
  if (JSON.stringify(prevSubmission) === JSON.stringify(nextSubmission)) return;

  const msg = {
    id: `submission-${nextSubmission.id}-${Date.now()}`,
    entityId: nextSubmission.id,
    modelType: ModelType.SUBMISSION,
    createdAt: new Date().toISOString(),
    changes: diffSubmissionFields(prevSubmission, nextSubmission),
  };

  logger?.log?.("enqueueSubmissionNotifications:", "New notification:", msg);

  setNotifications((prev) => [...prev, msg]);
  scheduleNotificationExpiry(msg, setNotifications);
}

/**
 * Single entry point for the two diff-notification cases (Projects.meta.users,
 * Submissions) — keeps the branching here, not inline in DatastoreHelpers.js.
 */
export function enqueueDiffNotifications({
  type,
  prevElement,
  nextElement,
  setNotifications,
  currentView,
  logger,
}) {
  if (type === "Projects") {
    enqueueMetaUsersNotifications({
      prevProject: prevElement,
      nextProject: nextElement,
      setNotifications,
      currentView,
      logger,
    });
  } else if (type === "Submissions") {
    enqueueSubmissionNotifications({
      prevSubmission: prevElement,
      nextSubmission: nextElement,
      setNotifications,
      currentView,
      logger,
    });
  }
}

/**
 * Runs the two per-event enqueue calls (each a no-op unless its own gate matches)
 * as one call, so the subscription callback doesn't make two.
 */
export function enqueueLiveEventNotifications({
  changeType,
  recordChange,
  setTickerItems,
  setNotifications,
  currentProjectToken,
  reviewingTokens,
  currentView,
  logger,
  appUser,
}) {
  enqueueTickerMessage({
    changeType,
    recordChange,
    setTickerItems,
    currentProjectToken,
    reviewingTokens,
    logger,
    appUser,
  });

  enqueueNotification({
    changeType,
    recordChange,
    setNotifications,
    currentView,
    logger,
  });
}
